import { http } from './http'
import { auth } from './auth'
import {
  AgendaNoteSchema,
  ItemSchema,
  ItemVariableSchema,
  RenderedVersionSchema,
  VersionSchema,
  VersionRenderedVariableSchema,
  VersionDetectedVariableSchema,
} from '../schemas'
import { parseWithZod } from '../utils/parse-with-zod'
import z from 'zod'

const ItemResponseSchema = z.object({ item: ItemSchema })
const PullDmsToVersionResponseSchema = z.object({ version: VersionSchema })

const ItemVariableResponseSchema = z.object({
  item_variables: z.array(ItemVariableSchema),
})

const RenderedVersionsResponseSchema = z.object({
  rendered_versions: z.array(RenderedVersionSchema),
})

const VersionRenderedVariablesResponseSchema = z.object({
  version_rendered_variables: z.array(VersionRenderedVariableSchema),
})

const VersionDetectedVariablesResponseSchema = z.object({
  version_detected_variables: z.array(VersionDetectedVariableSchema),
})

export const items = {
  getItemVariables: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/item_variables`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(ItemVariableResponseSchema, result)
    return parsedResult.item_variables
  },

  getRenderedVersions: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/rendered_versions`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(RenderedVersionsResponseSchema, result)
    return parsedResult.rendered_versions
  },

  getAgendaNotes: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/agenda_notes`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(z.object({ agenda_notes: z.array(AgendaNoteSchema) }), result)
    return parsedResult.agenda_notes
  },

  getVersionRenderedVariables: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/version_rendered_variables`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(VersionRenderedVariablesResponseSchema, result)
    return parsedResult.version_rendered_variables
  },

  getVersionDetectedVariables: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/version_detected_variables`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(VersionDetectedVariablesResponseSchema, result)
    return parsedResult.version_detected_variables
  },

  syncDms: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/items/${itemId}/sync_dms`, {}, dealId)
    return parseWithZod(ItemResponseSchema, result).item
  },

  pullDmsToVersion: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/items/${itemId}/pull_dms_to_version`,
      {},
      dealId,
    )
    return parseWithZod(PullDmsToVersionResponseSchema, result).version
  },

  clearSignaturePageNeedsReview: async (
    itemId: number,
    signaturePageIds: number[],
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(
      `${regionHost}/api/items/${itemId}/clear_signature_page_needs_review`,
      { signature_page_ids: signaturePageIds },
      dealId,
    )
  },

  setDmsDocument: async (
    itemId: number,
    payload: {
      document_id: string
      workspace_id?: number | null
      library_id?: string
      file_size?: number
      last_user_description?: string
      version?: number
      name?: string
      extension?: string
      edit_date?: string | null
    },
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/items/${itemId}/set_dms_document`,
      payload,
      dealId,
    )
    return parseWithZod(ItemResponseSchema, result).item
  },
}
